import React, { memo } from 'react';
import Snackbar, { SnackbarOrigin } from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import SnackbarContent from '@mui/material/SnackbarContent';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ErrorIcon from '@mui/icons-material/Error';

interface BaseSnackbarProps {
	open: boolean;
	message: string;
	onClose: () => void;
	autoHideDuration?: number;
	anchorOrigin?: SnackbarOrigin;
	severity?: 'success' | 'info' | 'warning' | 'error';
}

const BaseSnackbar = ({
	open,
	message,
	onClose,
	autoHideDuration = 5000,
	anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
	severity = 'success'
}: BaseSnackbarProps) => {
	const { vertical, horizontal } = anchorOrigin;
	const theme = useTheme();
	const { t } = useTranslation();
	const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
		if (reason === 'clickaway') {
			return;
		}
		onClose();
	};

	const getColorSeverity = () => {
		switch (severity) {
			case 'success':
				return theme.palette.grey[900];
			case 'info':
				return theme.palette.info.main;
			case 'warning':
				return theme.palette.warning.main;
			case 'error':
				return theme.palette.error.main;
			default:
				return theme.palette.grey[900];
		}
	};

	return (
		<Snackbar
			anchorOrigin={{ vertical, horizontal }}
			autoHideDuration={autoHideDuration}
			open={open}
			onClose={handleClose}
			key={vertical + horizontal}
		>
			<SnackbarContent
				sx={{ background: getColorSeverity() }}
				message={
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{severity === 'error' && <ErrorIcon />} <Typography>{t(message)}</Typography>
					</Box>
				}
				action={
					<IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
						<CloseIcon fontSize="small" />
					</IconButton>
				}
			/>
		</Snackbar>
	);
};

export default memo(BaseSnackbar);
